// @flow strict
/* eslint-disable react-hooks/exhaustive-deps */

import React, { useState, useEffect } from 'react'
import { type PropsWithChildren } from '@mondough/public-web-components/src/utils/typeUtils'
import { SectionWrapper, SectionThemes } from '../SectionWrapper'
import styles from './ECBRates.module.scss'

const BASE_URL = process.env.GATSBY_API_BASE_PATH
const OAUTH_ID = process.env.GATSBY_OAUTH_ID
const OAUTH_SECRET = process.env.GATSBY_OAUTH_SECRET

// We have two API calls so let's only call the auth token once and store it
const getAndStoreAuthToken = async setAuthToken => {
  try {
    const authTokenResponse = await fetch(`${BASE_URL}/oauth2/token`, {
      body: new URLSearchParams({
        grant_type: 'client_credentials',
        client_id: OAUTH_ID,
        client_secret: OAUTH_SECRET,
      }),
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        Accept: 'application/json',
      },
    })
    if (authTokenResponse.ok) {
      const response = await authTokenResponse.json()
      setAuthToken(response.access_token)
      return response.access_token
    }
  } catch (err) {
    console.error(err)
  }
}

const prettyDate = (s: string) => {
  const [year, month, day] = s.split('-')

  if (year == null || month == null || day == null) {
    return null
  }

  const date = new Date(
    parseInt(year, 10),
    // months in javascript are 0 based ugh
    parseInt(month, 10) - 1,
    parseInt(day, 10),
  )

  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date)
}

type CurrencyDetails = {|
  ecb_rate: string,
  ecb_rate_date: string,
  mc_rate: string,
  mc_rate_date: string,
  mc_markup_pct: string,
|}

type MastercardCurrency = {|
  alpha3: string,
  name: string,
  emoji: string,
  is_ecb: boolean,
|}

type AllRates = {| currencies: MastercardCurrency[] |}

// ECB = European Central Bank
const ECBRates = ({ children }: PropsWithChildren<{}>) => {
  const [isGetCurrenciesLoading, setIsGetCurrenciesLoading] = useState<boolean>(
    true,
  )
  const [authToken, setAuthToken] = useState(undefined)
  const [
    isCurrencyDetailLoading,
    setIsCurrencyDetailLoading,
  ] = useState<boolean>(true)
  const [
    allMastercardCurrencies,
    setAllMastercardCurrencies,
  ] = useState<?AllRates>(undefined)
  const [activeCurrency, setActiveCurrency] = useState<?string>('EUR')
  const [
    activeCurrencyDetails,
    setActiveCurrencyDetails,
  ] = useState<?CurrencyDetails>()

  // Get all currencies list
  useEffect(() => {
    const getAllMastercardCurrencies = async () => {
      const authTokenResponse = await getAndStoreAuthToken(setAuthToken)
      try {
        const allCurrenciesResponse =
          authTokenResponse &&
          (await fetch(`${BASE_URL}/fx-rates/mastercard/GBP`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${authTokenResponse}`,
            },
          }))
        if (allCurrenciesResponse && allCurrenciesResponse.ok) {
          const results = await allCurrenciesResponse.json()
          setAllMastercardCurrencies(results)
        }
      } catch (err) {
        console.error(err)
      }
    }
    if (isGetCurrenciesLoading) {
      getAllMastercardCurrencies()
      setIsGetCurrenciesLoading(false)
    }
  }, [])

  // Get specific currency details
  useEffect(() => {
    const fetchCurrencyDetails = async currencyCode => {
      let authTokenResponse
      if (authToken == null) {
        authTokenResponse = await getAndStoreAuthToken(setAuthToken)
      }
      try {
        const currencyDetailsResponse =
          (authToken || authTokenResponse) &&
          currencyCode &&
          (await fetch(`${BASE_URL}/fx-rates/mastercard/GBP/${currencyCode}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${authToken || authTokenResponse || ''}`,
            },
          }))

        if (currencyDetailsResponse) {
          const results = await currencyDetailsResponse.json()
          setActiveCurrencyDetails(results)
        }
      } catch (err) {
        console.error(err)
      }
    }
    if (isCurrencyDetailLoading) {
      fetchCurrencyDetails(activeCurrency)
      setIsCurrencyDetailLoading(false)
    }
  }, [isCurrencyDetailLoading, activeCurrency])

  return (
    <SectionWrapper
      theme={SectionThemes.LIGHT}
      contentClassName={styles.content}
    >
      <div>
        {children}

        {isGetCurrenciesLoading ? (
          <div className={styles.center}>Loading...</div>
        ) : (
          <>
            <div>
              <label
                htmlFor="currency-selector"
                className={styles.dropdownLabel}
              >
                Please choose a currency to see our exchange rate.
              </label>
              <div className={styles.dropdown}>
                <select
                  id="currency-selector"
                  name="currencies"
                  value={activeCurrency}
                  onChange={e => {
                    if (e.currentTarget.value !== activeCurrency) {
                      setActiveCurrency(e.currentTarget.value)
                      setIsCurrencyDetailLoading(true)
                    }
                  }}
                >
                  {allMastercardCurrencies &&
                    allMastercardCurrencies.currencies && (
                      <>
                        <optgroup label="European Central Bank currencies">
                          {allMastercardCurrencies.currencies
                            .filter(r => r.is_ecb)
                            .map(rate => {
                              return (
                                <option
                                  value={rate.alpha3}
                                  key={rate.alpha3}
                                  // ℹ️ Alpha3 is the ISO 3-letter currency code
                                >{`${rate.alpha3} - ${rate.name}`}</option>
                              )
                            })}
                          |{' '}
                        </optgroup>
                        <optgroup label="Other currencies">
                          {allMastercardCurrencies.currencies
                            .filter(r => !r.is_ecb)
                            .map(rate => (
                              <option value={rate.alpha3} key={rate.alpha3}>{`${
                                rate.alpha3
                              } - ${rate.name}`}</option>
                            ))}
                        </optgroup>
                      </>
                    )}
                </select>
              </div>
            </div>

            <div>
              {isCurrencyDetailLoading ? (
                <div className={styles.center}>Loading...</div>
              ) : (
                activeCurrency &&
                activeCurrencyDetails != null && (
                  <div>
                    <dl className={styles.ratesDataList}>
                      {activeCurrencyDetails.ecb_rate !== '' && (
                        <>
                          <dt>European Central Bank rate</dt>
                          <dd>{`1 GBP = ${
                            activeCurrencyDetails.ecb_rate
                          } ${activeCurrency}`}</dd>
                          <p className={styles.dateInfo}>
                            <span>Date updated:</span>{' '}
                            {prettyDate(activeCurrencyDetails.ecb_rate_date)}
                          </p>
                        </>
                      )}
                      <dt>Mastercard Rate</dt>
                      <dd>{`1 GBP = ${
                        activeCurrencyDetails.mc_rate
                      } ${activeCurrency}`}</dd>
                      <p className={styles.dateInfo}>
                        <span>Date updated:</span>{' '}
                        {prettyDate(activeCurrencyDetails.mc_rate_date)}
                      </p>
                      <dt>
                        Monzo mark-up <br />
                        over Mastercard rate
                      </dt>
                      <dd>{`0.00%`}</dd>
                      {activeCurrencyDetails.ecb_rate !== '' && (
                        <>
                          <dt>
                            Total mark-up <br />
                            over European Central Bank rate
                          </dt>
                          <dd>{activeCurrencyDetails.mc_markup_pct}%</dd>
                        </>
                      )}
                    </dl>
                  </div>
                )
              )}
            </div>
          </>
        )}
      </div>
    </SectionWrapper>
  )
}

export default ECBRates
